import {
  NeedDto,
  NeedsActionDto,
  NeedsHistoryEntryDto,
  NeedsResourceDetailsDto,
  NeedUpdateDto,
  NeedViewDto,
} from '@act/shared/data-transfer-objects';
import {
  NeedDeletedEventPayload,
  NeedEventTypes,
  NeedHistoryEntryUpsertedEventPayload,
  NeedUpsertedEventPayload,
} from '@act/shared/socket-events';
import {
  createAction,
  createEntityAdapter,
  createSelector,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';
import { needRequests } from 'redux/requests/need.requests';
import { useAppSelector } from 'redux/storeHooks';
import { NeedsState } from 'redux/types/needTypes';
import { createApiThunk } from 'redux/utils/apiThunk';
import { addToEffectsMiddleware } from 'redux/utils/effectsMiddleware.util';
import type { WebsocketEventActions } from '../types/websocketTypes';
import { resetRedux } from '../actions/sharedActions';
import { DateTime } from 'luxon';

// THUNKS/ACTIONS ==============================================================
export const getMemberNeeds = createApiThunk(needRequests.getNeedsForMember);
export const createNeed = createApiThunk(needRequests.createNeed);
export const patchNeed = createApiThunk(needRequests.patchNeed);
export const getExpiringAndNeedsActionUserNeedsForGuide = createApiThunk(
  needRequests.getExpiringAndNeedsActionUserNeedsByGuide
);
type LoadMemberNeedsPayload = { memberId: string };
export const loadMemberNeeds = createAction<LoadMemberNeedsPayload>(
  'needs/load-member-needs'
);

export const createNeedSuccess = createAction<NeedDto>(
  'needs/createNeedSuccess'
);
export const getExpiringAndNeedsActionUserNeedsForGuideSuccess = createAction<
  NeedDto[]
>('needs/getExpiringAndNeedsActionUserNeedsForGuideSuccess');
export const patchNeedSuccess = createAction<NeedDto>('needs/patchNeedSuccess');

// INITIAL STATE ===============================================================
export const needsAdapter = createEntityAdapter<NeedDto>();
export const actionAdapter = createEntityAdapter<NeedsActionDto>();
export const needsUpdateAdapter = createEntityAdapter<NeedUpdateDto>();
export const resourceDetailsAdapter =
  createEntityAdapter<NeedsResourceDetailsDto>();
export const historyAdapter = createEntityAdapter<NeedsHistoryEntryDto>();
export const expiringAndNeedsActionUserNeedsForGuideAdapter =
  createEntityAdapter<NeedDto>();

const initialState: NeedsState = {
  status: 'initial',
  needs: needsAdapter.getInitialState(),
  actions: actionAdapter.getInitialState(),
  needUpdates: needsUpdateAdapter.getInitialState(),
  resourceReferrals: resourceDetailsAdapter.getInitialState(),
  history: historyAdapter.getInitialState(),
  loadedMemberIds: {},
  expiringAndNeedsActionUserNeedsForGuide:
    expiringAndNeedsActionUserNeedsForGuideAdapter.getInitialState(),
};

// SLICE =======================================================================
export const needSlice = createSlice({
  name: 'need',
  initialState,
  reducers: {
    // need websockets =============================
    needUpserted: (
      state: NeedsState,
      action: PayloadAction<NeedUpsertedEventPayload>
    ) => {
      needsAdapter.upsertMany(state.needs, action.payload.needView.needs);
      actionAdapter.upsertMany(state.actions, action.payload.needView.actions);
      needsUpdateAdapter.upsertMany(
        state.needUpdates,
        action.payload.needView.needUpdates
      );
      resourceDetailsAdapter.upsertMany(
        state.resourceReferrals,
        action.payload.needView.resourceReferrals
      );
      historyAdapter.upsertMany(state.history, action.payload.needView.history);

      const actionPayload = action.payload.needView.actions;
      const hasRecentAction =
        actionPayload &&
        actionPayload.length > 0 &&
        actionPayload.find(
          action =>
            action.createdAt >= DateTime.now().minus({ days: 30 }).toMillis()
        );

      action.payload.needView.needs.forEach(need => {
        if (!hasRecentAction) {
          expiringAndNeedsActionUserNeedsForGuideAdapter.removeOne(
            state.expiringAndNeedsActionUserNeedsForGuide,
            need.id
          );
          expiringAndNeedsActionUserNeedsForGuideAdapter.upsertOne(
            state.expiringAndNeedsActionUserNeedsForGuide,
            need
          );
        } else {
          expiringAndNeedsActionUserNeedsForGuideAdapter.removeOne(
            state.expiringAndNeedsActionUserNeedsForGuide,
            need.id
          );
        }
      });

      return state;
    },
    needHistoryEntryUpserted: (
      state: NeedsState,
      action: PayloadAction<NeedHistoryEntryUpsertedEventPayload>
    ) => {
      const needsHistoryEntry = action.payload.needsHistoryEntry;
      const need = state.needs.entities[needsHistoryEntry.needId];

      if (action.payload.needsHistoryEntry) {
        historyAdapter.upsertOne(
          state.history,
          action.payload.needsHistoryEntry
        );
      }
      if (action.payload.needAction) {
        actionAdapter.upsertOne(state.actions, action.payload.needAction);
      }

      if (action.payload.needUpdate) {
        needsUpdateAdapter.upsertOne(
          state.needUpdates,
          action.payload.needUpdate
        );
      }

      if (action.payload.resourceDetails) {
        resourceDetailsAdapter.upsertOne(
          state.resourceReferrals,
          action.payload.resourceDetails
        );
      }
      if (!need.historyEntryIds.includes(needsHistoryEntry.id)) {
        need.historyEntryIds.push(action.payload.needsHistoryEntry.id);
        needsAdapter.upsertOne(state.needs, need);
      }

      return state;
    },
    userNeedDeleted: (
      state: NeedsState,
      action: PayloadAction<NeedDeletedEventPayload>
    ) => {
      needsAdapter.removeOne(state.needs, action.payload.needId);
      return state;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(resetRedux, state => {
        state = { ...initialState };
        return state;
      })
      .addCase(getMemberNeeds.pending, (state, action) => {
        state.status = 'loading';
        state.loadedMemberIds[action.meta.arg.params.memberId] = true;
      })
      .addCase(
        getMemberNeeds.fulfilled,
        (state, action: PayloadAction<NeedViewDto>) => {
          state.status = 'success';
          needsAdapter.upsertMany(state.needs, action.payload.needs);
          actionAdapter.upsertMany(state.actions, action.payload.actions);
          needsUpdateAdapter.upsertMany(
            state.needUpdates,
            action.payload.needUpdates
          );
          resourceDetailsAdapter.upsertMany(
            state.resourceReferrals,
            action.payload.resourceReferrals
          );
          historyAdapter.upsertMany(state.history, action.payload.history);
        }
      )
      .addCase(getMemberNeeds.rejected, (state, action) => {
        state.status = 'failed';
        state.loadedMemberIds[action.meta.arg.params.memberId] = false;
      })
      .addCase(createNeedSuccess, (state, action) => {
        needsAdapter.upsertOne(state.needs, action.payload);
        state.status = 'success';
      })
      .addCase(patchNeedSuccess, (state, action) => {
        needsAdapter.upsertOne(state.needs, action.payload);
        state.status = 'success';
      })
      .addCase(
        getExpiringAndNeedsActionUserNeedsForGuideSuccess,
        (state, action) => {
          expiringAndNeedsActionUserNeedsForGuideAdapter.removeAll(
            state.expiringAndNeedsActionUserNeedsForGuide
          );
          expiringAndNeedsActionUserNeedsForGuideAdapter.upsertMany(
            state.expiringAndNeedsActionUserNeedsForGuide,
            action.payload
          );
          state.status = 'success';
        }
      );
  },
});

// SELECTORS ===================================================================
interface NeedsSliceSelectorProps {
  memberId?: string;
  needHistoryEntryIds?: number[];
  [key: string]: unknown;
}

export const selectNeedsState = state =>
  (state as { need: typeof initialState })?.need;

export const selectNeedsAdapterState = state =>
  (state as { need: typeof initialState })?.need?.needs;

export const selectActionsAdapterState = state =>
  (state as { need: typeof initialState })?.need?.actions;

export const selectNeedUpdateAdapterState = state =>
  (state as { need: typeof initialState })?.need?.needUpdates;

export const selectNeedHistoryEntryAdapterState = state =>
  (state as { need: typeof initialState })?.need?.history;

export const selectResourceReferralsAdapterState = state =>
  (state as { need: typeof initialState })?.need?.resourceReferrals;

export const selectNeedsStatus = state =>
  (state as { need: typeof initialState })?.need?.status;

export const selectExpiringAndNeedsActionUserNeedsForGuideAdapterState =
  state =>
    (state as { need: typeof initialState })?.need
      ?.expiringAndNeedsActionUserNeedsForGuide;

const { selectAll: selectNeeds, selectById: selectNeedById } =
  needsAdapter.getSelectors(selectNeedsAdapterState);

export const { selectById: selectActionById } = actionAdapter.getSelectors(
  selectActionsAdapterState
);

export const { selectById: selectNeedUpdateById } =
  needsUpdateAdapter.getSelectors(selectNeedUpdateAdapterState);

export const { selectById: selectResourceDetailsById } =
  resourceDetailsAdapter.getSelectors(selectResourceReferralsAdapterState);

export const {
  selectAll: selectNeedHistoryEntries,
  selectById: selectNeedHistoryEntryById,
} = historyAdapter.getSelectors(selectNeedHistoryEntryAdapterState);

export const selectNeedHistoryEntriesByIds: (
  state,
  params: NeedsSliceSelectorProps
) => NeedsHistoryEntryDto[] = createSelector(
  [
    selectNeedHistoryEntries,
    (_state, params: NeedsSliceSelectorProps) => params.needHistoryEntryIds,
  ],
  (allHistoryEntries, needHistoryEntryIds) => {
    const history = allHistoryEntries.filter(n =>
      needHistoryEntryIds.includes(n.id)
    );
    if (!history) return [];

    return history.sort((a, b) => b.createdAt - a.createdAt);
  }
);

export const selectNeedsByMember: (
  state,
  params: NeedsSliceSelectorProps
) => NeedDto[] = createSelector(
  [selectNeeds, (_state, params: NeedsSliceSelectorProps) => params.memberId],
  (allNeeds, memberId) => {
    return allNeeds.filter(n => n.memberId === memberId);
  }
);

export const { selectAll: selectExpiringAndNeedsActionUserNeedsForGuide } =
  expiringAndNeedsActionUserNeedsForGuideAdapter.getSelectors(
    selectExpiringAndNeedsActionUserNeedsForGuideAdapterState
  );

// REDUCER =====================================================================
export const needReducer = needSlice.reducer;
export const needActions = needSlice.actions;

// EXPORT HELPER ===============================================================
export const needsSelectors = {
  useSelectNeedsByMember: (memberId: string) =>
    useAppSelector(state => selectNeedsByMember(state, { memberId })),
  useSelectNeedById: (needId: number) =>
    useAppSelector(state => selectNeedById(state, needId)),
  useSelectNeedHistoryEntryById: (needHistoryEntryId: number) =>
    useAppSelector(state =>
      selectNeedHistoryEntryById(state, needHistoryEntryId)
    ),
  useSelectNeedHistoryEntriesByIds: (needHistoryEntryIds: number[]) =>
    useAppSelector(state =>
      selectNeedHistoryEntriesByIds(state, { needHistoryEntryIds })
    ),
  useSelectNeedsStatus: () => useAppSelector(selectNeedsStatus),
  useSelectActionById: (actionId: number) =>
    useAppSelector(state => selectActionById(state, actionId)),
  useSelectNeedUpdateById: (needUpdateId: number) =>
    useAppSelector(state => selectNeedUpdateById(state, needUpdateId)),
  useSelectResourceDetailsById: (resourceDetailsId: number) =>
    useAppSelector(state =>
      selectResourceDetailsById(state, resourceDetailsId)
    ),
  useSelectExpiringAndNeedsActionUserNeedsForGuide: () =>
    useAppSelector(state =>
      selectExpiringAndNeedsActionUserNeedsForGuide(state)
    ),
};

// EFFECTS =====================================================================
addToEffectsMiddleware(addEffect => {
  addEffect({
    action: loadMemberNeeds,
    effect: async ({ dispatch, state, action }) => {
      const needState = state.need;
      const alreadyLoaded = needState.loadedMemberIds[action.payload.memberId];
      if (alreadyLoaded) return;
      dispatch(
        getMemberNeeds({ params: { memberId: action.payload.memberId } })
      );
    },
  });

  // Automatically dispatch success action when thunk completes
  addEffect({
    action: getExpiringAndNeedsActionUserNeedsForGuide.fulfilled,
    effect: async ({ dispatch, action }) => {
      dispatch(
        getExpiringAndNeedsActionUserNeedsForGuideSuccess(action.payload)
      );
    },
  });
});

// WEBSOCKET ACTIONS ======================================
export const needSliceWebsocketActions: WebsocketEventActions[] = [
  {
    type: NeedEventTypes.NEED_UPSERTED,
    action: needSlice.actions.needUpserted,
    channel: 'baseSocket',
  },
  {
    type: NeedEventTypes.NEED_HISTORY_ENTRY_UPSERTED,
    action: needSlice.actions.needHistoryEntryUpserted,
    channel: 'baseSocket',
  },
  {
    type: NeedEventTypes.NEED_DELETED,
    action: needSlice.actions.userNeedDeleted,
    channel: 'baseSocket',
  },
];
